'use client'

import { useTranslations } from 'next-intl'
import { AlertCircleIcon } from 'lucide-react'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Separator } from '@/components/ui/separator'

type Permission = {
  id: string
  name: string
  actions: {
    id: string
    name: string
  }[]
}

type PropsType = {
  permissions: Permission[] | undefined
  selectedActionIds: string[]
  onActionIdsChange: (actionIds: string[]) => void
  isLoading?: boolean
  emptyMessage?: string
}

const PermissionSelector = ({
  permissions,
  selectedActionIds,
  onActionIdsChange,
  isLoading = false,
  emptyMessage
}: PropsType) => {
  const t = useTranslations()

  const togglePermission = (actionIds: string[]) => {
    const allSelected = actionIds.every(id => selectedActionIds.includes(id))

    if (allSelected) {
      // Remove all action IDs for this permission
      onActionIdsChange(selectedActionIds.filter(id => !actionIds.includes(id)))
    } else {
      // Add all action IDs for this permission
      onActionIdsChange([...new Set([...selectedActionIds, ...actionIds])])
    }
  }

  const toggleAction = (actionId: string) => {
    if (selectedActionIds.includes(actionId)) {
      onActionIdsChange(selectedActionIds.filter(id => id !== actionId))
    } else {
      onActionIdsChange([...selectedActionIds, actionId])
    }
  }

  const isPermissionSelected = (actionIds: string[]) => {
    return actionIds.every(id => selectedActionIds.includes(id))
  }

  const isPermissionPartiallySelected = (actionIds: string[]) => {
    const selectedCount = actionIds.filter(id => selectedActionIds.includes(id)).length
    return selectedCount > 0 && selectedCount < actionIds.length
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    )
  }

  if (!permissions || permissions.length === 0) {
    return (
      <Alert>
        <AlertCircleIcon className="h-4 w-4" />
        <AlertDescription>{emptyMessage || t('no_options_available')}</AlertDescription>
      </Alert>
    )
  }

  return (
    <div className="space-y-4">
      {permissions.map((permission: Permission) => {
        const actionIds = permission.actions.map((a: any) => a.id)
        const allSelected = isPermissionSelected(actionIds)
        const partiallySelected = isPermissionPartiallySelected(actionIds)

        return (
          <div key={permission.id} className="rounded-lg border p-4">
            <div className="mb-3 flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox
                  id={permission.id}
                  checked={allSelected ? true : partiallySelected ? 'indeterminate' : false}
                  onCheckedChange={() => togglePermission(actionIds)}
                />
                <Label htmlFor={permission.id}>{t(permission.name.toLowerCase())}</Label>
              </div>
            </div>
            <Separator className="mb-3" />
            <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
              {permission.actions.map((action: any) => (
                <div key={action.id} className="flex items-center space-x-2">
                  <Checkbox
                    id={action.id}
                    checked={selectedActionIds.includes(action.id)}
                    onCheckedChange={() => toggleAction(action.id)}
                  />
                  <Label htmlFor={action.id} className="font-normal">
                    {t(`permission_actions_${action.name.toLowerCase()}`)}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export default PermissionSelector
